import React, { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Home, ChevronRight, FolderInput, Loader2 } from 'lucide-react';
import { getFoldersWithThumbnails, getFolderPath } from '../services/libraryService';

/**
 * MoveToFolderModal Component
 * A modal for selecting a destination folder, with support for nested folder navigation
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback when modal is closed
 * @param {function} props.onSelectFolder - Callback when a folder is selected (receives folderId or null for root)
 * @param {string|null} props.currentFolderId - Current folder the item is in (to exclude from options)
 * @param {number} props.itemCount - Number of items being moved
 * @param {string} props.itemName - Name of single item being moved (if itemCount is 1)
 * @param {boolean} props.isMoving - Whether a move operation is in progress
 * @param {string} props.userId - Current user's ID
 * @param {string} props.brandId - Current brand's ID
 * @param {string} props.activeTab - Active library tab ('media', 'ads', 'casting', 'locations')
 */
const MoveToFolderModal = ({
  isOpen,
  onClose,
  onSelectFolder,
  currentFolderId,
  itemCount = 1,
  itemName = '',
  isMoving = false,
  userId,
  brandId,
  activeTab
}) => {
  const [browsingFolderId, setBrowsingFolderId] = useState(null);
  const [browsingPath, setBrowsingPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get the item type for folder filtering
  const getItemType = useCallback(() => {
    if (activeTab === 'media') return 'image';
    if (activeTab === 'ads') return 'ad_creative';
    if (activeTab === 'casting') return 'casting';
    return 'location';
  }, [activeTab]);

  // Load folders for current browsing location
  const loadFolders = useCallback(async () => {
    if (!userId || !brandId) return;

    setIsLoading(true);
    try {
      const itemType = getItemType();
      const loadedFolders = await getFoldersWithThumbnails(userId, brandId, itemType, browsingFolderId);
      setFolders(loadedFolders);
    } catch (error) {
      console.error('Failed to load folders:', error);
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, brandId, browsingFolderId, getItemType]);

  // Load folder path for breadcrumb
  const loadPath = useCallback(async () => {
    if (!browsingFolderId || !userId) {
      setBrowsingPath([]);
      return;
    }

    try {
      const path = await getFolderPath(browsingFolderId, userId);
      setBrowsingPath(path);
    } catch (error) {
      console.error('Failed to load folder path:', error);
      setBrowsingPath([]);
    }
  }, [browsingFolderId, userId]);

  // Load folders when modal opens or browsing location changes
  useEffect(() => {
    if (isOpen) {
      loadFolders();
      loadPath();
    }
  }, [isOpen, loadFolders, loadPath]);

  // Reset browsing state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBrowsingFolderId(null);
      setBrowsingPath([]);
    }
  }, [isOpen]);

  const handleNavigateToRoot = () => {
    setBrowsingFolderId(null);
  };

  const handleNavigateToFolder = (folder) => {
    setBrowsingFolderId(folder.id);
  };

  const handleDoubleClickFolder = (folder) => {
    // Navigate into the folder on double-click
    setBrowsingFolderId(folder.id);
  };

  const handleSelectFolder = (folderId) => {
    onSelectFolder(folderId);
  };

  const handleMoveToRoot = () => {
    onSelectFolder(null);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '450px',
          padding: 'var(--space-6)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ marginBottom: 'var(--space-4)' }}>Move to Folder</h3>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
          {itemCount > 1
            ? `Select a destination for ${itemCount} items`
            : `Select a destination for "${itemName}"`}
        </p>

        {/* Breadcrumb navigation for browsing */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
          padding: 'var(--space-2)',
          background: 'var(--color-bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          flexWrap: 'wrap',
          fontSize: 'var(--font-size-sm)'
        }}>
          <button
            onClick={handleNavigateToRoot}
            className="btn btn-ghost btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              padding: 'var(--space-1) var(--space-2)',
              fontWeight: browsingFolderId ? 'normal' : 'bold'
            }}
          >
            <Home size={14} />
            Root
          </button>
          {browsingPath.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <ChevronRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
              {index === browsingPath.length - 1 ? (
                <span style={{ fontWeight: 500, padding: 'var(--space-1) var(--space-2)' }}>{folder.name}</span>
              ) : (
                <button
                  onClick={() => handleNavigateToFolder(folder)}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 'var(--space-1) var(--space-2)' }}
                >
                  {folder.name}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Folder list */}
        <div style={{ flex: 1, overflow: 'auto', marginBottom: 'var(--space-4)' }}>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-6)' }}>
              <Loader2 size={24} className="animate-spin" style={{ color: 'var(--color-primary)' }} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {/* Move to current location button */}
              {browsingFolderId !== currentFolderId && (
                <button
                  className="btn btn-primary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    justifyContent: 'center',
                    padding: 'var(--space-3)'
                  }}
                  onClick={() => handleSelectFolder(browsingFolderId)}
                  disabled={isMoving}
                >
                  {isMoving ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <FolderInput size={18} />
                  )}
                  <span>
                    {browsingFolderId
                      ? `Move here (${browsingPath[browsingPath.length - 1]?.name || 'Folder'})`
                      : 'Move to Root'}
                  </span>
                </button>
              )}

              {/* Subfolder list */}
              {folders.length === 0 && browsingFolderId !== currentFolderId ? (
                <p style={{ color: 'var(--color-text-tertiary)', textAlign: 'center', padding: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                  No subfolders here
                </p>
              ) : folders.length === 0 ? (
                <p style={{ color: 'var(--color-text-tertiary)', textAlign: 'center', padding: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                  No folders yet. Create a folder first.
                </p>
              ) : (
                folders.map(folder => (
                  <button
                    key={folder.id}
                    className="btn btn-secondary"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      justifyContent: 'flex-start',
                      padding: 'var(--space-3)',
                      opacity: folder.id === currentFolderId ? 0.5 : 1
                    }}
                    onClick={() => handleDoubleClickFolder(folder)}
                    disabled={isMoving || folder.id === currentFolderId}
                    title={folder.id === currentFolderId ? 'Item is already in this folder' : 'Click to browse, select "Move here" to move'}
                  >
                    <FolderOpen size={18} style={{ color: folder.color }} />
                    <span style={{ flex: 1, textAlign: 'left' }}>{folder.name}</span>
                    <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-xs)' }}>
                      {folder.itemCount || 0} items
                    </span>
                    <ChevronRight size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isMoving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveToFolderModal;
